import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useStore } from "@/lib/useStore";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StorefrontLayout from "@/components/layouts/StorefrontLayout";
import ContentCard from "@/components/ContentCard";
import { Forbidden, NotFound } from "@/pages/ErrorPages";
import { ListVideo } from "lucide-react";
import { toast } from "sonner";

export default function MyList() {
  const { slug, sp } = useStore();
  const { user, status, logout } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [pageStatus, setPageStatus] = useState("loading");
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get(`/public/tenants/${slug}`)
      .then((r) => { setTenant(r.data); setPageStatus("ready"); })
      .catch(() => setPageStatus("not_found"));
  }, [slug]);

  useEffect(() => {
    if (status !== "authenticated") return;
    api.get("/me/watchlist")
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || e.message));
  }, [status]);

  if (status === "loading" || pageStatus === "loading") {
    return <div className="min-h-screen bg-[#0a0a0a] grid place-items-center text-white/40 text-sm uppercase tracking-widest">Loading…</div>;
  }
  if (pageStatus === "not_found" || !tenant) return <NotFound />;
  if (status !== "authenticated" || !user) return <Navigate to={sp(`/login`)} replace />;
  if (user.role === "subscriber" && user.tenant_id !== tenant.id) return <Forbidden />;

  // Filter to this tenant's items only
  const filtered = items.filter((w) => w.content && w.content.tenant_id === tenant.id);

  return (
    <StorefrontLayout tenant={tenant} currentUser={user} onLogout={logout} showSubscriberNav>
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-12" data-testid="my-list-page">
        <p className="text-[11px] uppercase tracking-[0.4em] text-white/40">{tenant.name}</p>
        <h1 className="mt-2 font-display font-bold text-3xl md:text-5xl tracking-tighter">My list</h1>
        <p className="mt-2 text-white/50 font-light">Saved titles you can come back to anytime.</p>

        <div className="mt-12">
          {filtered.length === 0 ? (
            <div className="border border-dashed border-white/15 p-12 text-center" data-testid="my-list-empty">
              <ListVideo className="w-8 h-8 mx-auto text-white/40" />
              <h2 className="mt-4 font-display font-bold text-2xl">Your list is empty.</h2>
              <p className="mt-2 text-white/50 text-sm font-light">Browse the catalogue and tap "+ Add to My list" on any title.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="my-list-grid">
              {filtered.map((w) => (
                <ContentCard
                  key={w.id}
                  item={w.content}
                  tenantSlug={tenant.slug}
                  variant="poster"
                  themeColor={tenant.theme_color}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </StorefrontLayout>
  );
}
